import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authService } from '../services/api';

const AuthContext = createContext(null);

const AUTH_TOKEN = 'authToken';
const ADMIN_USER = 'adminUser';
const MUST_CHANGE_PASSWORD = 'mustChangePassword';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const u = localStorage.getItem(ADMIN_USER);
      return u ? JSON.parse(u) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(() => localStorage.getItem(AUTH_TOKEN));
  const [mustChangePassword, setMustChangePassword] = useState(() =>
    localStorage.getItem(MUST_CHANGE_PASSWORD) === 'true'
  );
  const [authReady, setAuthReady] = useState(!!localStorage.getItem(AUTH_TOKEN));

  const persistAuth = useCallback((newToken, newUser, mustChange) => {
    if (newToken) localStorage.setItem(AUTH_TOKEN, newToken);
    else localStorage.removeItem(AUTH_TOKEN);
    if (newUser) localStorage.setItem(ADMIN_USER, JSON.stringify(newUser));
    else localStorage.removeItem(ADMIN_USER);
    localStorage.setItem(MUST_CHANGE_PASSWORD, mustChange ? 'true' : 'false');
    setToken(newToken);
    setUser(newUser);
    setMustChangePassword(!!mustChange);
  }, []);

  const login = useCallback(
    async (username, password) => {
      const { data } = await authService.login(username, password);
      const t = data.token || data.access;
      const raw = data.user || {};
      const u = {
        id: raw.id ?? data.id,
        username: raw.username ?? data.username ?? username,
        role: raw.role ?? data.role ?? 'admin',
      };
      const mustChange = data.must_change_password === true || data.mustChangePassword === true;
      persistAuth(t, u, mustChange);
      return { mustChangePassword: mustChange };
    },
    [persistAuth]
  );

  const logout = useCallback(() => {
    authService.logout();
    persistAuth(null, null, false);
  }, [persistAuth]);

  const changePassword = useCallback(
    async (currentPassword, newPassword) => {
      await authService.changePassword(currentPassword, newPassword);
      persistAuth(token, user, false);
    },
    [persistAuth, token, user]
  );

  useEffect(() => {
    if (!token) {
      setAuthReady(true);
      return;
    }
    authService
      .getMe()
      .then(({ data }) => {
        const u = data.user || data;
        if (u && (u.role == null || u.role === '')) {
          u.role = data.role ?? 'admin';
        }
        const must = data.must_change_password === true || data.mustChangePassword === true;
        setUser(u);
        setMustChangePassword(must);
        if (u) localStorage.setItem(ADMIN_USER, JSON.stringify(u));
        localStorage.setItem(MUST_CHANGE_PASSWORD, must ? 'true' : 'false');
      })
      .catch(() => {
        persistAuth(null, null, false);
      })
      .finally(() => setAuthReady(true));
  }, [token, persistAuth]);

  const value = {
    user,
    token,
    mustChangePassword,
    authReady,
    login,
    logout,
    changePassword,
    isAuthenticated: !!token,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
